import React from 'react';
import { Idol, ExtendedStat } from 'types';
import { safeGetItemProperty } from 'utils';
import { getTextFromModId } from 'utils/mod-utils';

interface TooltipProps {
  item: Idol | null;
  position: { x: number; y: number };
}

const Tooltip: React.FC<TooltipProps> = ({ item, position }) => {
  if (!item) return null;
  
  // Calculate better positioning based on screen location
  // If we're on the right side of the screen, place tooltip to the left of cursor instead
  const isNearRightEdge = position.x > window.innerWidth - 300;
  const tooltipX = isNearRightEdge 
    ? Math.max(10, position.x - 275) // Position to left of cursor when near right edge
    : Math.min(window.innerWidth - 260, position.x + 25); // Otherwise to the right
  
  const tooltipY = Math.min(Math.max(10, position.y - 150), window.innerHeight - 200);
  
  return (
    <div 
      className="fixed bg-gray-800 p-3 rounded-lg shadow-lg z-50 border border-gray-700"
      style={{
        width: '250px',
        left: `${tooltipX}px`,
        top: `${tooltipY}px`,
        pointerEvents: 'none'
      }}
    >
      <div className="font-semibold mb-2 text-white">{safeGetItemProperty(item, 'name', 'Unknown Item')}</div>
      {Array.isArray(item.stats) && item.stats.map((stat, idx) => {
        if (!stat) return null;
        
        // Cast to our extended stat type to access originalId
        const extendedStat = stat as ExtendedStat;

        let displayText = getTextFromModId(extendedStat.modId) || "Unknown modifier";
        
        // Format the description with proper value
        if (displayText) {
          // Replace patterns like "(150–200)" or "(0.5–0.7)" with the actual value
          displayText = displayText.replace(/(\+?\()(\d+(\.\d+)?)–(\d+(\.\d+)?)(\))/g, (match, prefix) => {
            // Keep the + if it exists in the prefix
            const plusSign = prefix.includes('+') ? '+' : '';
            // Check if the original match contained a '%' sign
            const hasPercentSign = match.includes('%');
            return `${plusSign}${extendedStat.value}${hasPercentSign ? '%' : ''}`; // Add % only if it was in the original text
          });
        }
        
        // Determine text color based on prefix/suffix
        let textColorClass = "text-blue-400"; // Default
        
        // Check for originalId (should be available for newly created items)
        if (extendedStat.explicitType) {
          if (extendedStat.explicitType.startsWith('prefix')) {
            textColorClass = "text-[#ffa9ff]"; // Pink color for prefixes
          } else if (extendedStat.explicitType.startsWith('suffix') || 
                    extendedStat.explicitType.includes('suffix')) {
            textColorClass = "text-[#a9ffff]"; // Cyan color for suffixes
          }
        } 
        
        return (
          <div key={idx} className={`${textColorClass} mb-1.5 text-sm`}>
            {displayText}
          </div>
        );
      })}
    </div>
  );
};

export default Tooltip;